import { FormAdapter, FormAlert, FormContainerOptions, FormControlProperties, FormControlType, FormValues } from '@omegagrid/form';
import { ColorTypes } from '@omegagrid/core';
import { msg } from '@omegagrid/localize';
import rp, { helpers } from '@reporting/rp-app-host';

/** certificate status of the group custom domain (GET group/domain, no secrets) */
type DomainStatus = {
	custom_domain?: string;
	id_domain?: number;
	cert_authority?: string;
	cert_issued?: string;
	cert_expiration?: string;
	acme_status?: string;
};

/**
 * Certificate of the accounting group's custom domain - shows the current state
 * and lets a group admin upload an own certificate (pfx) or request a Let's
 * Encrypt one. Both write the P_DOMAIN row of the domain on the server.
 */
export class DomainCertificateFormAdapter extends FormAdapter {

	private status: DomainStatus = {};

	getColumns() {
		return [
			{width: 140},
			{width: null},
		];
	}

	async getControls(): Promise<FormControlProperties[]> {
		this.status = await rp.ms('domain').get<DomainStatus>('group/domain');

		const certificate = this.status.cert_expiration
			? `${this.status.cert_authority ?? ''} · ${msg('Expiration')}: ${this.status.cert_expiration}`
			: msg('The domain has no certificate');

		const rows: FormControlProperties[] = [
			{row: 0, col: 0, type: FormControlType.Label, value: msg('Domain')},
			{row: 0, col: 1, type: FormControlType.Label, value: this.status.custom_domain ?? ''},
			{row: 1, col: 0, type: FormControlType.Label, value: msg('Certificate')},
			{row: 1, col: 1, type: FormControlType.Label, value: certificate},
		];

		if (this.status.acme_status) {
			rows.push({row: 2, col: 0, type: FormControlType.Label, value: msg('ACME status')});
			rows.push({row: 2, col: 1, type: FormControlType.Label, value: this.status.acme_status});
		}

		rows.push({row: 3, col: 0, type: FormControlType.Label, value: msg('Certificate file (pfx)'), for: 'pfx'});
		rows.push({row: 3, col: 1, type: FormControlType.FileInput, key: 'pfx', value: null});
		rows.push({row: 4, col: 0, type: FormControlType.Label, value: msg('Password'), for: 'password'});
		rows.push({row: 4, col: 1, type: FormControlType.PasswordInput, key: 'password', value: null});

		return rows;
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			closeAfterSave: false,
			buttons: [
				{key: 'letsencrypt', text: msg('Request Let\'s Encrypt certificate'), color: ColorTypes.Accent},
				{key: 'save', text: msg('Upload certificate'), color: ColorTypes.Green, default: true},
				'close',
			],
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		const file = formValues.pfx as File;
		if (!file) {
			return [{type: 'warning', text: msg('Select the certificate file first')}];
		}

		try {
			const base64Content = await helpers.fileToDataUrl(file);
			await rp.ms('domain').post('group/domain/certificate', {
				pfx: base64Content.split(',')[1],
				password: (formValues.password as string) ?? '',
			});
		} catch (error) {
			return [{type: 'danger', text: (error as Error).message ?? String(error)}];
		}

		rp.host.bucket.showAlert({type: 'success', content: msg('Certificate installed')});
		await this.refresh();
		return true;
	}

	async onAction(action: string) {
		if (action !== 'letsencrypt') return;

		const command = await rp.host.openMenu({
			content: 'yes_no',
			text: `${msg('Request Let\'s Encrypt certificate')}: ${this.status.custom_domain}?`,
		});
		if (command?.id != 'Y') return;

		try {
			await rp.ms('domain').post('group/domain/letsencrypt', {});
			rp.host.bucket.showAlert({type: 'success', content: msg('Certificate issued')});
		} catch (error) {
			rp.host.bucket.showAlert({
				type: 'danger',
				title: msg('Certificate request failed'),
				content: (error as Error).message ?? String(error),
			});
		}

		await this.refresh();
	}

	/** re-renders the status labels after an install/request */
	private async refresh() {
		this.component?.setSourceData({
			controls: await this.getControls(),
			columns: this.getColumns(),
		});
	}

}
